import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

export const ENGAGEMENT_ID = 'demo_engagement';
const STORAGE_KEY = 'demoPos.branches';
const LAST_BILL_BRANCH_KEY = 'demoPos.lastBillBranch';

const BranchContext = createContext(null);

function readStored() {
  try {
    const raw = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    return Array.isArray(raw) ? raw.map(String) : null;
  } catch {
    return null;
  }
}

function store(key, value) {
  try {
    window.localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  } catch {
    /* private window / blocked storage: the selection just isn't remembered */
  }
}

export function rememberBillBranch(id) {
  store(LAST_BILL_BRANCH_KEY, String(id || ''));
}

export function lastBillBranch() {
  try {
    return window.localStorage.getItem(LAST_BILL_BRANCH_KEY) || '';
  } catch {
    return '';
  }
}

/**
 * Branch dropdown state shared by every page: "Demo Engagement" (ticked by default) plus every
 * Nyafaz branch. The selection filters the queue, the bills list and analytics.
 */
export function BranchProvider({ children }) {
  const [all, setAll] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => readStored() || [ENGAGEMENT_ID]);

  useEffect(() => {
    let alive = true;
    api
      .branches()
      .then((items) => {
        if (!alive) return;
        setAll(items);
        setError('');
        // Drop remembered ids that no longer exist.
        const known = new Set(items.map((b) => String(b.id)));
        setSelected((prev) => prev.filter((id) => known.has(id)));
      })
      .catch((e) => alive && setError(e.message || 'Could not load branches'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => store(STORAGE_KEY, selected), [selected]);

  const toggle = useCallback((id) => {
    const key = String(id);
    setSelected((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  }, []);

  const value = useMemo(() => {
    const branches = all.filter((b) => b.type === 'branch');
    const byId = Object.fromEntries(all.map((b) => [String(b.id), b]));
    const selectedBranches = branches.filter((b) => selected.includes(String(b.id)));
    return {
      all,
      branches,
      byId,
      loading,
      error,
      selected,
      selectedBranches,
      engagementSelected: selected.includes(ENGAGEMENT_ID),
      /** Comma list for `?branches=` — always sent, so an empty selection really means "nothing". */
      param: selected.join(','),
      toggle,
      setSelected,
      selectAll: () => setSelected(all.map((b) => String(b.id))),
      clear: () => setSelected([]),
      branchName: (id) => byId[String(id)]?.name || (id ? `Branch ${id}` : ''),
    };
  }, [all, selected, loading, error, toggle]);

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
}

export function useBranches() {
  const ctx = useContext(BranchContext);
  if (!ctx) throw new Error('useBranches must be used inside <BranchProvider>');
  return ctx;
}
